import { LitElement, html, css, classMap, unsafeHTML } from '@hh/global.js'

class HHNavMobileButton extends LitElement {
  static properties = {
    open: {
      reflect: true,
    },

    title: {
      type: String,
    },
  }

  constructor() {
    super()
    this.body = document.querySelector('body')
    this.mutationObserver = new MutationObserver((mutationsList) =>
      this.handleBodyMutation(mutationsList)
    )
  }

  connectedCallback() {
    super.connectedCallback()
    this.observeBodyMutation()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.disconnectObserver()
  }

  observeBodyMutation() {
    const target = document.body
    const config = { attributes: true, childList: true, subtree: true }
    this.mutationObserver.observe(target, config)
  }

  disconnectObserver() {
    this.mutationObserver.disconnect()
  }

  handleBodyMutation(mutationsList) {
    mutationsList.forEach((mutation) => {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'class'
      ) {
        const bodyClass = mutation.target.className
        if (bodyClass.includes('mini-cart-is-active')) {
          this.toggleButtonDupe()
        }
      }
    })
  }

  render() {
    return html`<button
      @click="${this.toggleButton}"
      class="${classMap({ 'is-active': this.open })}" aria-label="${this.title}"
    >
      <span class="sr-only">${this.title}</span>
    </button>`
  }

  toggleButton() {
    this.setHeaderHeight()
    this.open = !this.open
    this.dispatchEvent(new CustomEvent('toggle', { detail: this.open }))
    if (this.open == true) {
      this.body.setAttribute('style', 'overflow: hidden;')
      this.body.classList.add('overflow-hidden')
      this.body.classList.add('mobile-menu-open')
    } else {
      this.body.setAttribute('style', '')
      this.body.classList.remove('overflow-hidden')
      this.body.classList.remove('mobile-menu-open')
    }
  }

  toggleButtonDupe() {
    const isOpen = this.getAttribute('open')
    if (isOpen == 'true') {
      this.removeAttribute('open')
      this.body.setAttribute('style', '')
      this.body.classList.remove('overflow-hidden')
      this.body.classList.remove('mobile-menu-open')
    }
  }

  setHeaderHeight() {
    const root = document.documentElement
    const header = document.querySelector('.js-header-wrapper')
    const headerHeight = header.getBoundingClientRect().height
    root.style.setProperty('--hh-header-height', `${headerHeight}px`)
  }

  static styles = css`
    :host {
    }

    button {
      background-color: var(--hh-color-teal);
      display: block;
      height: 1px;
      width: 23px;
      padding: 0;
      border: 10px solid var(--hh-color-reverse);
      border-top-width: 25px;
      border-bottom-width: 25px;
      transition: 0.2s all;
      position: relative;
      box-sizing: content-box;
    }

    body.header-sticky button {
      border-top-width: 15px;
      border-bottom-width: 15px;
    }

    button::before,
    button::after {
      content: '';
      background-color: var(--hh-color-teal);
      display: block;
      height: 1px;
      width: 100%;
      transition: 0.2s all;
      position: absolute;
      left: 0;
    }

    button::before {
      top: 7px;
    }

    button::after {
      top: -7px;
    }

    button.is-active {
      background-color: transparent;
    }

    button.is-active::before {
      top: 0;
      transform: rotate(45deg);
    }

    button.is-active::after {
      top: 0;
      transform: rotate(-45deg);
    }

    .sr-only {
      display: block;
      height: 0;
      overflow: hidden;
      text-indent: -9999px;
      width: 0;
    }
  `
}

class HHNavMobile extends LitElement {
  static properties = {
    open: {
      reflect: true,
    },
    itemsEncoded: {
      type: String,
    },
    menuTitleColorsEncoded: {
      type: String,
    },
    items: {
      type: Array,
    },
    pane2Open: {
      type: Boolean,
    },
    pane2Content: {
      type: String,
    },
    pane3Open: {
      type: Boolean,
    },
    pane3Content: {
      type: String,
    },
  }

  constructor() {
    super()
    this.mutationObserver = new MutationObserver((mutationsList) =>
      this.handleBodyMutation(mutationsList)
    )
    this.menuTitleColors = {}
  }

  connectedCallback() {
    this.items = JSON.parse(atob(this.itemsEncoded))
    const colorsEncoded =
      this.menuTitleColorsEncoded || this.getAttribute('menuTitleColorsEncoded')
    if (colorsEncoded) {
      this.menuTitleColors = this.parseMenuTitleColors(atob(colorsEncoded))
    }
    super.connectedCallback()
    this.observeBodyMutation()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.disconnectObserver()
  }

  observeBodyMutation() {
    const target = document.body
    const config = { attributes: true, childList: true, subtree: true }
    this.mutationObserver.observe(target, config)
  }

  disconnectObserver() {
    this.mutationObserver.disconnect()
  }

  handleBodyMutation(mutationsList) {
    mutationsList.forEach((mutation) => {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'class'
      ) {
        const bodyClass = mutation.target.className
        if (bodyClass.includes('mini-cart-is-active')) {
          this.toggleButtonDupe()
        }
      }
    })
  }

  toggleButtonDupe() {
    const isOpen = this.getAttribute('open')
    if (isOpen == 'true') {
      this.removeAttribute('open')
    }
  }

  get iconSvg() {
    const slot = this.shadowRoot.querySelector('slot[name="arrow-icon"]')
    if (!slot) return ''
    const nodes = slot.assignedNodes()
    return unsafeHTML(nodes[0].innerHTML)
  }

  get footer() {
    const slot = this.shadowRoot.querySelector('slot[name="footer"]')
    if (!slot) return ''
    const nodes = slot.assignedNodes()
    return unsafeHTML(nodes[0].innerHTML)
  }

  parseMenuTitleColors(raw) {
    if (!raw) return {}
    const map = {}
    raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line && line.includes(':'))
      .forEach((line) => {
        const parts = line.split(':')
        const title = parts[0] ? parts[0].trim() : ''
        const color = parts[1] ? parts[1].trim() : ''
        if (!title || !color) return
        map[this.handleize(title)] = color
      })
    return map
  }

  handleize(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  getMenuTitleColor(title) {
    const key = this.handleize(title)
    return this.menuTitleColors && this.menuTitleColors[key]
      ? this.menuTitleColors[key]
      : ''
  }

  selectTier1(item) {
    this.pane2Open = true
    if (item.links && item.links.length) {
      this.pane2Content = html`<ul>
        <li class="back">
          <button
            @click="${() => {
              this.pane2Open = false
            }}"
            aria-label="${item.title}"
          >
            <span class="back-arrow"> ${this.iconSvg} </span>
            ${item.title}
          </button>
        </li>
        ${item.links.map(
          (link) => html`<li>
            <button
              @click="${() => {
                this.selectTier2(link)
              }}"
              aria-label="${link.title}"
            >
              ${link.title}
            </button>

            ${link.links && link.links.length > 0
              ? html`<span class="arrow"> ${this.iconSvg} </span>`
              : ''}
          </li>`
        )}
      </ul>`
      this.pane2Open = true
      return
    }

    if (item.url) {
      this.open = false
      window.location = item.url
    }
  }

  selectTier2(item) {
    this.pane3Open = true
    if (item.links && item.links.length) {
      this.pane3Content = html`<ul>
        <li class="back">
          <button
            @click="${() => {
              this.pane3Open = false
            }}"
            aria-label="${item.title}"
          >
            <span class="back-arrow"> ${this.iconSvg} </span>
            ${item.title}
          </button>
        </li>
        ${item.links.map(
          (link) => html`<li>
            <button
              @click="${() => {
                this.selectTier3(link)
              }}"
              aria-label="${link.title}"
            >
              ${link.title}
            </button>
          </li>`
        )}
      </ul>`
      this.pane3Open = true
      return
    }

    if (item.url) {
      this.open = false
      window.location = item.url
    }
  }

  selectTier3(item) {
    if (item.url) {
      this.open = false
      window.location = item.url
    }
  }

  render() {
    return html` <div>
        <div class="hidden">
          <slot name="arrow-icon"></slot>
        </div>
        <hh-navigation-mobile-button
          title="${this.open ? 'Close Navigation' : 'Open Navigation'}"
          @toggle=${({ detail }) => {
            this.open = detail
          }}
        />
      </div>
      <div role="dialog" aria-label="mobile navigation dropdown" aria-modal="true" tabindex="-1">
        <hh-focus-trap active="${this.open}">
          <div
            class="${classMap({
              menu: true,
              open: this.open,
              'pane2-open': this.pane2Open,
              'pane3-open': this.pane3Open,
            })}"
            tabindex="0"
          >
            <div class="pane-content">
              <div class="pane1">
                <ul>
                  ${this.items.map(
                    (item) => html`<li class="navigation-mobile__item">
                      <button
                        @click="${() => {
                          this.selectTier1(item)
                        }}"
                        aria-label="${item.title}"
                        style="${this.getMenuTitleColor(item.title)
                          ? `color: ${this.getMenuTitleColor(item.title)};`
                          : ''}"
                      >
                        ${item.title}
                      </button>
                      ${item.links && item.links.length > 0
                        ? html`<span class="arrow"> ${this.iconSvg} </span>`
                        : ''}
                    </li>`
                  )}
                </ul>
                <div class="footer">
                  <slot name="footer"></slot>
                </div>
              </div>
              <div class="pane2">
                <div>${this.pane2Content}</div>
                <div class="footer">${this.footer}</div>
              </div>
              <div class="pane3">
                <div>${this.pane3Content}</div>
                <div class="footer">${this.footer}</div>
              </div>
            </div>
          </div>
        </hh-focus-trap>
      </div>`
  }

  static styles = css`
    :host {
    }

    ::slotted() .navigation-mobile__footer__item__link,
    .navigation-mobile__footer__item__link {
      display: block;
      padding: 12px 24px;
      text-decoration: none;
      color: inherit;
      text-decoration: none;
      text-transform: uppercase;
    }

    .hidden {
      display: none;
    }

    .menu {
      position: fixed;
      background-image: url('//teaforteshop.myshopify.com/cdn/shop/files/pyramid_140x.png?v=1623327553');
      height: calc(var(--vh, 1vh) * 100 - var(--hh-header-height, 152px));
      display: flex;
      flex-direction: row;
      top: var(--hh-header-height, 152px);
      left: 0;
      right: 0;
      bottom: 0;
      transform: translateX(-100%);
      transition: transform 0.25s;
      overflow-y: auto;
      overflow-x: hidden;
      display: block;
      visibility: hidden;
      z-index: 200;
    }

    .menu.open {
      visibility: visible;
      transform: translateX(0);
    }

    .pane1,
    .pane2,
    .pane3 {
      position: absolute;
      top: 0;
      width: 100%;
      left: 0;
      transition: left 0.3s;
    }

    .pane2,
    .pane3 {
      left: 100%;
      transition: left 0.3s;
    }

    .pane2-open .pane1 {
      left: -100%;
    }

    .pane3-open .pane1 {
      left: -200%;
    }

    .pane2-open .pane2 {
      left: 0;
    }

    .pane3-open .pane3 {
      left: 0;
    }

    .pane3-open .pane2 {
      left: -100%;
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      margin-bottom: 20px;
    }

    li {
      position: relative;
      width: auto;
      border-bottom: 1px solid var(--hh-color-gray);
      background-color: var(--hh-color-reverse);
      display: flex;
      justify-content: space-between;
      padding: 24px;
    }

    li.back {
      color: var(--hh-color-reverse);
      text-align: center;
      background-color: var(--hh-color-teal);
    }

    li.back .back-arrow {
      width: 12px;
      height: 7px;
      left: 4px;
      position: absolute;
      transform: rotate(90deg);
      top: calc(50% - 3.5px);
    }

    li.back button {
      position: relative;
      background-color: var(--hh-color-teal);
      color: var(--hh-color-reverse);
      text-align: center;
      display: block;
    }

    li button {
      font-family: var(--hh-font-family);
      letter-spacing: 1px;
      font-size: 12px;
      font-weight: 300;
      text-transform: uppercase;
      width: 100%;
      padding: 0;
      text-align: left;
      border: 0;
      background-color: var(--hh-color-reverse);
      display: block;
      position: relative;
      cursor: pointer;
      display: flex;
      color: var(--hh-color-teal);
    }

    .arrow {
      width: 12px;
      height: 7px;
      margin: auto 0;
      right: 24px;
      transform: rotate(-90deg);
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .arrow svg,
    .back-arrow svg {
      width: 100%;
      height: 100%;
    }

    .navigation-mobile__footer__items {
      margin-bottom: 60px;
    }

    .navigation-mobile__footer__item {
      padding: 12px 24px;
      text-decoration: none;
      text-transform: uppercase;
      line-height: 16px;
      font-size: 12px;
      letter-spacing: 1px;
      font-weight: 300;
      border-bottom: none;
      background-color: unset;
    }

    .navigation-mobile__footer__item a {
      text-decoration: none;
      color: inherit;
    }

    .navigation-mobile__item button {
      color: var(--hh-color-teal);
    }
    .navigation-mobile__item button[aria-label='Semi-Annual Event'],
    .navigation-mobile__item button[aria-label='Sale'],
    .navigation-mobile__item button[aria-label='Savings'] {
      color: #8A6F28;
    }
  `
}

customElements.define('hh-navigation-mobile-button', HHNavMobileButton)
customElements.define('hh-navigation-mobile', HHNavMobile)
